import { AppError } from '@app/errors/AppError'

import { getAbortSignal } from '@app/packages/abortContext/actions'
import { withChildContext } from '@app/packages/actionContext/actions'
import type { ApiActionCallback, ApiSession } from '@app/packages/api/utils'
import { getApiActionDescriptor } from '@app/packages/api/utils'
import type { InternalApiActionCallback } from '@app/packages/internalApi/utils'

import { sessionTeardownSelector } from '@app/store/slices/session'
import { updateFromResponseSlice } from '@app/store/slices/update_from_response'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import { getApiDispatcher } from './contexts/apiDispatcher'
import { getApiInterceptor } from './contexts/apiInterceptor'
import { getInternalApiDispatcher } from './contexts/internalApiDispatcher'

/** Polygons and tests stub a call by its label; everything else goes to the dispatcher. */
export const callApi = <R>(apiAction: ApiActionCallback<R>) =>
  createThunk(async (dispatch, getState): Promise<R> => {
    const { label } = getApiActionDescriptor(apiAction)
    const options = { abortSignal: dispatch(getAbortSignal()), session: dispatch(apiSession()) }
    for (const interceptor of dispatch(getApiInterceptor()).interceptors) {
      const stub = interceptor({ label, action: apiAction, options })
      if (stub === 'pass') break
      if (stub) return (await stub()) as R
    }
    try {
      return await dispatch(getApiDispatcher()).dispatch(apiAction, options)
    } catch (e) {
      throw handledDuringTeardown(e, sessionTeardownSelector(getState()))
    }
  })

/**
 * Every `callApi` inside sends without our session — no bearer, and no `X-Kidsout-Session-Id`,
 * so the response is the same for everyone and may be cached across viewers.
 *
 * Ambient like `withAbortSignal`, rather than an argument: a call several thunks down needs no
 * option threaded to it, and the scope ends with the action it wraps.
 */
export function withAnonymous<R>(action: ThunkAction<R>) {
  return withApiSession({ type: 'anonymous' }, action)
}

/** Every `callApi` inside goes out as another session, signed with its jwt; ours is neither read nor refreshed for it. */
export function withSession<R>(token: string, action: ThunkAction<R>) {
  return withApiSession({ type: 'explicit', token }, action)
}

/**
 * A v2 endpoint, through the same dispatcher as v3 — the base url already differs per
 * environment. The entities the response carried along are folded in explicitly, rather than
 * sniffed out of the action by a reducer.
 */
export const callV2Api = <R>(apiAction: ApiActionCallback<R>) =>
  createThunk(async dispatch => {
    const payload = await dispatch(callApi(apiAction))
    dispatch(updateFromResponseSlice(payload))
    return payload
  })

export const callInternalApi = <R>(apiAction: InternalApiActionCallback<R>) =>
  createThunk(dispatch => {
    const dispatcher = dispatch(getInternalApiDispatcher())
    return dispatcher.dispatch(apiAction, { abortSignal: dispatch(getAbortSignal()) })
  })

const withApiSession = <R>(session: ApiSession, action: ThunkAction<R>) =>
  withChildContext(
    createThunk((dispatch, _getState, context) => {
      context.set(SessionSymbol, session)
      return dispatch(action)
    })
  )

const apiSession = () => createThunk((_dispatch, _getState, context) => context.lookup<ApiSession>(SessionSymbol))

const SessionSymbol = Symbol('api_session')

/**
 * A 401 once the session has been destroyed is the teardown window, not a fault. The cookies are
 * gone but React keeps rendering until the page reloads, so every selector that loads on render
 * fires once more and comes back unauthorised — 39 Sentry issues and 1073 events in 90 days.
 *
 * Only 401: anything else going wrong mid-logout is still worth seeing.
 */
const handledDuringTeardown = (e: unknown, teardown: boolean) => {
  if (teardown && e instanceof AppError && e.status === 401) return e.withHandled(true)
  return e
}
